import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils, type VRM } from '@pixiv/three-vrm';
import {
  VRMAnimationLoaderPlugin,
  createVRMAnimationClip,
  type VRMAnimation,
} from '@pixiv/three-vrm-animation';
import { useVrm } from '../context/VrmContext';

interface Props {
  /** 再生する .vrma の URL（復号済み）。undefined ならクリップを表示せず呼び出し側のプレースホルダに委ねる。 */
  vrmaUrl?: string;
  /** 再生中かどうか。 */
  playing: boolean;
  /** 再生位置の通知（およそ250ms間隔。duration は AnimationClip.duration）。 */
  onProgress?: (elapsedSec: number, durationSec: number) => void;
  /** VRM/VRMA の読み込みに失敗したときに呼ばれる。 */
  onError?: () => void;
}

/**
 * ShortsPlayer が呼び出し側（Shorts.tsx）に公開するカメラ操作 API。
 * このコンポーネントはフィードの背後に隠れて直接ポインタイベントを受け取れないため、
 * ジェスチャー認識は Shorts.tsx 側で行い、結果（ピクセル差分やスケール）だけをここへ渡してもらう。
 */
export interface ShortsPlayerHandle {
  /** 画面ピクセル差分でカメラを方位角方向にオービットさせる（縦方向の回転は封印しており、横方向のみ）。 */
  orbit(dxPx: number): void;
  /** 注視点からの距離を scale で割る（scale > 1 で寄る、scale < 1 で引く）。 */
  dolly(scale: number): void;
  /** 画面ピクセル差分で、カメラのローカル right/up 軸に沿って注視点とカメラを平行移動する。 */
  pan(dxPx: number, dyPx: number): void;
  /** 指を離したときに呼ぶ。px/ミリ秒 で渡ってくる横方向の速度から慣性回転を開始する（十分速い場合のみ）。 */
  endOrbit(vxPxPerMs: number): void;
  /** 進行中の慣性回転を即座に停止する。次の操作を始める前に呼ぶ。 */
  stopInertia(): void;
}

/** カメラの注視点からの距離（オービット半径）の可動範囲。 */
const MIN_RADIUS = 0.6;
const MAX_RADIUS = 6;
/** 1px あたりの回転量（rad）。 */
const ORBIT_SENSITIVITY = 0.005;
/** 慣性回転の減衰の時定数(秒)。velocity *= exp(-delta/TAU) */
const ORBIT_INERTIA_TAU = 0.32;
/** これ(px/秒)を下回ったら慣性停止。 */
const ORBIT_INERTIA_MIN_SPEED = 25;
/** 放した瞬間これ(px/秒)未満なら慣性を始めない（微小移動でスッと動くのを防ぐ）。 */
const ORBIT_INERTIA_MIN_FLING_SPEED = 100;

/**
 * 球面座標でのカメラオービット本体。画面ピクセル差分 dxPx を方位角の回転量に変換し、
 * camera.position/quaternion を直接書き換える。縦方向（仰角）の回転は封印しており、
 * 常に横方向のみ回す。手動ドラッグ（orbit）と慣性（animate ループ）の両方から同じ計算を
 * 使い回すため、モジュールレベルのヘルパーとして切り出している。
 */
function applyOrbit(camera: THREE.PerspectiveCamera, target: THREE.Vector3, dxPx: number) {
  const offset = new THREE.Vector3().subVectors(camera.position, target);
  const spherical = new THREE.Spherical().setFromVector3(offset);
  // OrbitControls と同じ符号（右へドラッグ→方位角が減る）。
  spherical.theta -= dxPx * ORBIT_SENSITIVITY;
  offset.setFromSpherical(spherical);
  camera.position.copy(target).add(offset);
  camera.lookAt(target);
}

function createLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));
  loader.register((parser) => new VRMAnimationLoaderPlugin(parser));
  return loader;
}

/**
 * Shorts フィード専用の 3D プレーヤー。
 * フィード全体の背後に敷く「1インスタンスだけ」の Three.js シーンで、アイテムが切り替わっても
 * シーン・カメラ・VRM は読み直さず、VRMA クリップだけを差し替える（無限スクロールでも重くならない
 * ようにするため）。VrmaViewer.tsx とは別実装で、縦スワイプと競合しないよう OrbitControls は
 * 使わない。タップでの再生/一時停止トグルは呼び出し側（Shorts.tsx）が担当する
 * （このコンポーネントはフィードの後ろに隠れており、直接クリックを受け取れないため）。
 */
const ShortsPlayer = forwardRef<ShortsPlayerHandle, Props>(function ShortsPlayer(
  { vrmaUrl, playing, onProgress, onError },
  ref,
) {
  const { vrmUrl, ready } = useVrm();
  const containerRef = useRef<HTMLDivElement>(null);

  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  // カメラの注視点。オービット/パンの基準点として使う（初期値は camera.lookAt の初期値と揃える）。
  const targetRef = useRef(new THREE.Vector3(0, 1.0, 0));
  // 慣性回転用の状態。orbitVelRef は横方向の画面ピクセル速度(px/秒)、inertiaActiveRef は
  // 慣性が進行中かどうか（縦方向の回転は封印しているため縦成分は保持しない）。
  const orbitVelRef = useRef({ vx: 0 });
  const inertiaActiveRef = useRef(false);
  const vrmRef = useRef<VRM>();
  const mixerRef = useRef<THREE.AnimationMixer>();
  const actionRef = useRef<THREE.AnimationAction>();
  const clipDurationRef = useRef(0);
  const pendingVrmaRef = useRef<string | undefined>(vrmaUrl);
  // 「今アバターを表示すべきか」。vrmaUrl の有無だけで決まる（読み込み中/失敗時も vrmaUrl が
  // あれば表示したままにする）。VRM 本体の非同期読み込みが vrmaUrl の変化より遅れて完了しても
  // 正しい可視状態を反映できるよう、loadVrm の完了時にもこの ref を参照する。
  const desiredVisibleRef = useRef(!!vrmaUrl);

  // playing / コールバックは render ループやクロージャから最新値を参照したいので ref に控える。
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
    if (actionRef.current) actionRef.current.paused = !playing;
  }, [playing]);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // ---- Three.js 初期化（マウント時に一度だけ）----
  useEffect(() => {
    const container = containerRef.current!;
    const width = container.clientWidth || 1;
    const height = container.clientHeight || 1;

    const scene = new THREE.Scene();
    // scene.background はあえて null のまま（CSS側のティールグロー背景を透かすため）。
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 1.3, 2.6);
    camera.lookAt(targetRef.current);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    // alpha: true だけでは背景クリア色のアルファは既定で不透明のままなので、明示的に透過にする。
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    // ライティングは VrmaViewer.tsx と同じ設定。
    const hemi = new THREE.HemisphereLight(0xffffff, 0x444455, 1.2);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 1.4);
    dir.position.set(1, 2, 1.5);
    scene.add(dir);

    // 床グリッド（VrmaViewer.tsx と同じワイヤフレーム調の床。色も揃える）
    const grid = new THREE.GridHelper(10, 20, 0x335555, 0x223333);
    scene.add(grid);

    const clock = new THREE.Clock();
    let raf = 0;
    // 再生位置の通知は毎フレーム呼ぶと過剰なので、250ms 間隔に間引く。
    let sinceLastProgress = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (mixerRef.current && playingRef.current) mixerRef.current.update(delta);
      vrmRef.current?.update(delta);

      // 慣性回転: 指を離した後も直近の角速度で回転を続け、指数減衰で自然に停止させる。
      if (inertiaActiveRef.current && cameraRef.current) {
        const v = orbitVelRef.current;
        applyOrbit(cameraRef.current, targetRef.current, v.vx * delta);
        const decay = Math.exp(-delta / ORBIT_INERTIA_TAU);
        v.vx *= decay;
        if (Math.abs(v.vx) < ORBIT_INERTIA_MIN_SPEED) inertiaActiveRef.current = false;
      }

      renderer.render(scene, camera);

      if (actionRef.current && clipDurationRef.current > 0) {
        sinceLastProgress += delta;
        if (sinceLastProgress >= 0.25) {
          sinceLastProgress = 0;
          onProgressRef.current?.(actionRef.current.time, clipDurationRef.current);
        }
      }
    };
    animate();

    const ro = new ResizeObserver(() => {
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.dispose();
      renderer.domElement.remove();
      if (vrmRef.current) VRMUtils.deepDispose(vrmRef.current.scene);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- カメラ操作 API（Shorts.tsx のジェスチャー認識から呼ばれる）----
  // ここでの計算結果は camera.position/quaternion に直接書き込むだけで、render ループ（上の
  // animate()）が次フレームで自動的に描画してくれるため、React の再レンダーは一切挟まない。
  useImperativeHandle(
    ref,
    () => ({
      orbit(dxPx) {
        // ドラッグ中に残留慣性が混ざらないよう、手動操作の開始時点で必ず止めておく。
        inertiaActiveRef.current = false;
        const camera = cameraRef.current;
        if (!camera) return;
        applyOrbit(camera, targetRef.current, dxPx);
      },
      dolly(scale) {
        const camera = cameraRef.current;
        if (!camera || !Number.isFinite(scale) || scale <= 0) return;
        const target = targetRef.current;
        const offset = new THREE.Vector3().subVectors(camera.position, target);
        const radius = THREE.MathUtils.clamp(offset.length() / scale, MIN_RADIUS, MAX_RADIUS);
        offset.setLength(radius);
        camera.position.copy(target).add(offset);
        camera.lookAt(target);
      },
      pan(dxPx, dyPx) {
        const camera = cameraRef.current;
        const container = containerRef.current;
        if (!camera || !container) return;
        // 直前のオービット/パン操作でまだ反映されていない position/quaternion を matrix に
        // 焼き込む（render ループを待つと1フレーム分古い基底ベクトルを使ってしまうため）。
        camera.updateMatrixWorld();

        const target = targetRef.current;
        const distance = camera.position.distanceTo(target);
        const height = container.clientHeight || 1;
        const fovRad = THREE.MathUtils.degToRad(camera.fov);
        // OrbitControls.pan と同じ式: 画面の高さいっぱいの移動が targetDistance*tan(fov/2)*2 に対応する。
        const scale = (2 * distance * Math.tan(fovRad / 2)) / height;

        const e = camera.matrix.elements;
        // camera.matrix の列0/列1 がカメラのローカル right/up 軸をワールド座標で表す。
        const right = new THREE.Vector3(e[0], e[1], e[2]).multiplyScalar(-dxPx * scale);
        const up = new THREE.Vector3(e[4], e[5], e[6]).multiplyScalar(dyPx * scale);
        const pan = right.add(up);

        const nextTarget = target.clone().add(pan);
        nextTarget.x = THREE.MathUtils.clamp(nextTarget.x, -2, 2);
        nextTarget.z = THREE.MathUtils.clamp(nextTarget.z, -2, 2);
        nextTarget.y = THREE.MathUtils.clamp(nextTarget.y, 0, 2.5);
        // クランプで実際に動いた分だけカメラも平行移動する（クランプしなかった場合は pan と同じ）。
        const actualPan = nextTarget.clone().sub(target);
        target.copy(nextTarget);
        camera.position.add(actualPan);
        camera.lookAt(target);
      },
      endOrbit(vxPxPerMs) {
        // px/ミリ秒 → px/秒 に変換して保持し、十分速い（フリック相当の）場合のみ慣性を開始する
        // （わずかな残留速度でスッと動いてしまうのを防ぐため）。
        const vx = vxPxPerMs * 1000;
        orbitVelRef.current = { vx };
        if (Math.abs(vx) >= ORBIT_INERTIA_MIN_FLING_SPEED) inertiaActiveRef.current = true;
      },
      stopInertia() {
        inertiaActiveRef.current = false;
        orbitVelRef.current = { vx: 0 };
      },
    }),
    [],
  );

  // ---- VRM 読み込み ----
  async function loadVrm(url: string) {
    try {
      const loader = createLoader();
      const gltf = await loader.loadAsync(url);
      const vrm = gltf.userData.vrm as VRM;

      // 不要なジョイントを除去して負荷軽減。VRM0 系は後ろ向きなので回転（VrmaViewer.tsx と同様）。
      VRMUtils.removeUnnecessaryVertices(gltf.scene);
      VRMUtils.combineSkeletons(gltf.scene);
      VRMUtils.rotateVRM0(vrm);

      if (vrmRef.current) {
        sceneRef.current?.remove(vrmRef.current.scene);
        VRMUtils.deepDispose(vrmRef.current.scene);
      }
      vrmRef.current = vrm;
      sceneRef.current?.add(vrm.scene);
      // VRM の読み込みは非同期のため、完了した時点での「現在あるべき可視状態」を必ず反映する
      // （読み込み中に vrmaUrl が undefined に変わっていた場合、そのまま既定の visible=true で
      // 出てしまうと、フィードが空のときにも T ポーズが透けて見えてしまう）。
      vrm.scene.visible = desiredVisibleRef.current;
      mixerRef.current = new THREE.AnimationMixer(vrm.scene);
      actionRef.current = undefined;
      clipDurationRef.current = 0;

      // 先に来ていた VRMA があれば適用する。
      if (pendingVrmaRef.current) {
        const pending = pendingVrmaRef.current;
        pendingVrmaRef.current = undefined;
        await loadVrma(pending);
      }
    } catch (e) {
      console.error('Shorts: VRM の読み込みに失敗しました', e);
      onErrorRef.current?.();
    }
  }

  // ---- VRMA クリップの読み込み・差し替え ----
  const loadedVrmaUrlRef = useRef<string | undefined>(undefined);
  async function loadVrma(url: string) {
    if (!vrmRef.current || !mixerRef.current) {
      // VRM 未読み込みなら保留して、VRM 読み込み完了後に適用する。
      pendingVrmaRef.current = url;
      return;
    }
    try {
      const loader = createLoader();
      const gltf = await loader.loadAsync(url);
      const vrmAnimations = gltf.userData.vrmAnimations as VRMAnimation[] | undefined;
      if (!vrmAnimations || vrmAnimations.length === 0) {
        throw new Error('VRMAnimation が含まれていません');
      }
      const clip = createVRMAnimationClip(vrmAnimations[0], vrmRef.current);

      mixerRef.current.stopAllAction();
      const action = mixerRef.current.clipAction(clip);
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.play();
      action.paused = !playingRef.current;
      actionRef.current = action;
      clipDurationRef.current = clip.duration;
      vrmRef.current.scene.visible = desiredVisibleRef.current;
      loadedVrmaUrlRef.current = url;
    } catch (e) {
      console.error('Shorts: VRMA の読み込みに失敗しました', e);
      // クリップの読み込み失敗は「アニメーションが無い」だけであって「アバターが無い」わけではない。
      // vrmaUrl がある場合（＝desiredVisibleRef が true のはず）は非表示のままにせず可視へ戻す。
      // desiredVisibleRef を参照することで、読み込み中に vrmaUrl が undefined に変わっていた
      // （＝フィード側で既に非表示にしたい）レースでも誤って表示してしまわないようにする。
      if (vrmRef.current) vrmRef.current.scene.visible = desiredVisibleRef.current;
      onErrorRef.current?.();
    }
  }

  // Context の vrmUrl（選択中の VRM）が用意でき次第、一度だけ読み込む。
  const loadedVrmUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!ready || !vrmUrl || loadedVrmUrlRef.current === vrmUrl) return;
    loadedVrmUrlRef.current = vrmUrl;
    void loadVrm(vrmUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, vrmUrl]);

  // アクティブなアイテムの vrmaUrl が変わるたびにクリップを差し替える。
  useEffect(() => {
    if (!vrmaUrl) {
      // 再生できるクリップが無いアイテムでは、キャラクターを隠して呼び出し側のプレースホルダに委ねる。
      desiredVisibleRef.current = false;
      actionRef.current?.stop();
      mixerRef.current?.stopAllAction();
      actionRef.current = undefined;
      clipDurationRef.current = 0;
      pendingVrmaRef.current = undefined;
      loadedVrmaUrlRef.current = undefined;
      // VRM がまだ読み込み中（vrmRef.current 未設定）のときはここで何もできないが、
      // desiredVisibleRef を false にしてあるので、後から loadVrm が完了した時点で
      // 正しく非表示が反映される（vrmaUrl 自体はそのまま変化しないため、この effect は再実行されない）。
      if (vrmRef.current) vrmRef.current.scene.visible = false;
      return;
    }
    // 読み込み中・失敗中にかかわらず、vrmaUrl があるアイテムでは表示したままにする
    // （切り替え直後の解決待ちでキャラクターが一瞬消えるちらつきを避けるため）。
    desiredVisibleRef.current = true;
    if (vrmRef.current) vrmRef.current.scene.visible = true;
    if (loadedVrmaUrlRef.current === vrmaUrl) return;
    void loadVrma(vrmaUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vrmaUrl]);

  return <div className="shorts-stage" ref={containerRef} />;
});

export default ShortsPlayer;
